import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { useSnackbar } from "notistack";

import backendClient from "src/api";
import { Button } from "src/components/ui/button";
import useIsCompact from "src/hooks/useIsCompact";
import useLocalStorage from "src/hooks/useLocalStorage";

import { useWebSocketContext } from "./WebSocketContext";

// Polling interval for running jobs (15 seconds)
const HEARTBEAT_POLL_INTERVAL_MS = 15 * 1000;

// Types
export type JobStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "error";

/**
 * Controls the "View Results" button behavior in Jobs page:
 * - 'import': Opens AsyncImportResultsViewer (for flat file imports)
 * - 'url': Navigates to viewResultsUrl
 */
export type ViewResultsType = "import" | "url";

export interface BackgroundJob {
  jobId: string;
  label: string;
  contextLabel?: string; // Namespace name for display
  entityLabel?: string; // Entity name for display
  status: JobStatus;

  // Hierarchy for organization
  clusterId: string;
  namespaceId?: string;
  entityId?: string;

  // Metadata
  jobType: string;
  jobTypeLabel?: string; // Human-readable label for the job type
  startedAt: string;
  completedAt?: string;
  lastHeartbeat?: string; // For staleness detection

  // Results summary (from WebSocket payload)
  createdCount?: number;
  updatedCount?: number;
  unchangedCount?: number;
  skippedCount?: number;
  errorCount?: number;

  // Results viewing configuration (undefined = no "View Results" button)
  // Note: If a JobTypeRenderer with ResultsViewer is registered, that takes precedence
  viewResultsType?: ViewResultsType;
  viewResultsUrl?: string; // Required when viewResultsType='url'

  // Arbitrary metadata for custom renderers (passed to JobTypeRenderer.renderRowDetails)
  metadata?: Record<string, unknown>;

  // Generic result summary for display (e.g., "12 rows processed", "Report generated")
  // Falls back to computed summary from counts if not provided
  resultSummary?: string;
}

export interface BackgroundJobResult {
  jobId: string;
  status: "completed" | "failed";
  createdCount?: number;
  updatedCount?: number;
  errorCount?: number;
  payload?: Record<string, unknown>;
}

export interface RegisterJobParams {
  jobId: string;
  label: string;
  contextLabel?: string; // Namespace name for display
  entityLabel?: string; // Entity name for display
  jobType: string;
  jobTypeLabel?: string; // Human-readable label (e.g., "Entity Import")
  clusterId: string;
  namespaceId?: string;
  entityId?: string;
  onComplete?: (result: BackgroundJobResult) => void;
  resultsAction?: () => void;
  // Results viewing - persisted so it works after page refresh
  viewResultsType?: ViewResultsType;
  viewResultsUrl?: string;
  // Arbitrary metadata for custom renderers
  metadata?: Record<string, unknown>;
}

interface BackgroundJobsContextType {
  jobs: BackgroundJob[];
  registerJob: (params: RegisterJobParams) => void;
  removeJob: (jobId: string) => void;
  clearJobs: (clusterId?: string) => void;
  clearCompletedJobs: (clusterId?: string) => void;
  getJobsForCluster: (clusterId: string) => BackgroundJob[];
  getJobsForNamespace: (
    clusterId: string,
    namespaceId: string
  ) => BackgroundJob[];
  getRunningJobsCount: (clusterId?: string) => number;
}

const BackgroundJobsContext = createContext<BackgroundJobsContextType | null>(
  null
);

export const useBackgroundJobs = () => {
  const context = useContext(BackgroundJobsContext);
  if (!context) {
    throw new Error(
      "useBackgroundJobs must be used within BackgroundJobsProvider"
    );
  }
  return context;
};

// Storage key for localStorage
const STORAGE_KEY = "lumonic_background_jobs";

// Callback registry (not persisted to localStorage)
interface JobCallbacks {
  onComplete?: (result: BackgroundJobResult) => void;
  resultsAction?: () => void;
}

interface BackgroundJobsProviderProps {
  children: React.ReactNode;
}

export const BackgroundJobsProvider: React.FC<BackgroundJobsProviderProps> = ({
  children,
}) => {
  const { websocket } = useWebSocketContext();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const isCompact = useIsCompact();

  // Persisted job list (without callbacks)
  const [jobs, setJobs] = useLocalStorage<BackgroundJob[]>(STORAGE_KEY, []);

  // Callbacks stored in memory (not serializable)
  const callbacksRef = useRef<Map<string, JobCallbacks>>(new Map());

  // Register a new job
  const registerJob = useCallback(
    (params: RegisterJobParams) => {
      const newJob: BackgroundJob = {
        jobId: params.jobId,
        label: params.label,
        contextLabel: params.contextLabel,
        entityLabel: params.entityLabel,
        status: "pending",
        clusterId: params.clusterId,
        namespaceId: params.namespaceId,
        entityId: params.entityId,
        jobType: params.jobType,
        jobTypeLabel: params.jobTypeLabel,
        startedAt: new Date().toISOString(),
        viewResultsType: params.viewResultsType,
        viewResultsUrl: params.viewResultsUrl,
        metadata: params.metadata,
      };

      // Store callbacks in memory
      if (params.onComplete || params.resultsAction) {
        callbacksRef.current.set(params.jobId, {
          onComplete: params.onComplete,
          resultsAction: params.resultsAction,
        });
      }

      // Add to persisted list
      setJobs((prev) => [...prev, newJob]);
    },
    [setJobs]
  );

  // Remove a job
  const removeJob = useCallback(
    (jobId: string) => {
      callbacksRef.current.delete(jobId);
      setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
    },
    [setJobs]
  );

  // Clear all jobs (optionally by cluster)
  const clearJobs = useCallback(
    (clusterId?: string) => {
      if (clusterId) {
        // Clear callbacks for this cluster's jobs
        jobs
          .filter((j: BackgroundJob) => j.clusterId === clusterId)
          .forEach((j: BackgroundJob) => callbacksRef.current.delete(j.jobId));
        setJobs((prev: BackgroundJob[]) =>
          prev.filter((j: BackgroundJob) => j.clusterId !== clusterId)
        );
      } else {
        callbacksRef.current.clear();
        setJobs([]);
      }
    },
    [jobs, setJobs]
  );

  // Clear completed jobs (optionally by cluster)
  const clearCompletedJobs = useCallback(
    (clusterId?: string) => {
      const completedStatuses: JobStatus[] = ["completed", "failed", "error"];
      setJobs((prev: BackgroundJob[]) =>
        prev.filter((j: BackgroundJob) => {
          const isCompleted = completedStatuses.includes(j.status);
          const matchesCluster = clusterId ? j.clusterId === clusterId : true;
          if (isCompleted && matchesCluster) {
            callbacksRef.current.delete(j.jobId);
            return false;
          }
          return true;
        })
      );
    },
    [setJobs]
  );

  // Get jobs for a specific cluster
  const getJobsForCluster = useCallback(
    (clusterId: string) => {
      return jobs.filter((j: BackgroundJob) => j.clusterId === clusterId);
    },
    [jobs]
  );

  // Get jobs for a specific namespace
  const getJobsForNamespace = useCallback(
    (clusterId: string, namespaceId: string) => {
      return jobs.filter(
        (j: BackgroundJob) =>
          j.clusterId === clusterId && j.namespaceId === namespaceId
      );
    },
    [jobs]
  );

  // Get count of running jobs
  const getRunningJobsCount = useCallback(
    (clusterId?: string) => {
      const runningStatuses: JobStatus[] = ["pending", "running"];
      return jobs.filter(
        (j: BackgroundJob) =>
          runningStatuses.includes(j.status) &&
          (clusterId ? j.clusterId === clusterId : true)
      ).length;
    },
    [jobs]
  );

  // Listen to WebSocket for job updates
  useEffect(() => {
    if (!websocket) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        // Only handle BACKGROUND_JOB messages
        if (data.channel_type !== "BACKGROUND_JOB") return;

        const payload = data.payload;
        const jobId = payload.job_id;

        if (!jobId) return;

        // Update job status
        setJobs((prev: BackgroundJob[]) => {
          const jobIndex = prev.findIndex(
            (j: BackgroundJob) => j.jobId === jobId
          );

          if (jobIndex === -1) {
            // Job not found - might have been registered elsewhere or cleared
            // We could optionally add it here, but for now we'll skip
            return prev;
          }

          const updatedJobs = [...prev];
          updatedJobs[jobIndex] = {
            ...updatedJobs[jobIndex],
            status: payload.status,
            createdCount: payload.created_count,
            updatedCount: payload.updated_count,
            unchangedCount: payload.unchanged_count,
            skippedCount: payload.skipped_count,
            errorCount: payload.error_count,
            completedAt: payload.completed_at,
            lastHeartbeat: payload.last_heartbeat,
            resultSummary: payload.result_summary,
            // When job starts running, set initial heartbeat to started_at
            ...(payload.status === "running" && !payload.last_heartbeat
              ? { lastHeartbeat: payload.started_at }
              : {}),
          };

          return updatedJobs;
        });

        // If job completed, failed, or errored, trigger callbacks and show toast
        if (
          payload.status === "completed" ||
          payload.status === "failed" ||
          payload.status === "error"
        ) {
          const callbacks = callbacksRef.current.get(jobId);
          const job = jobs.find((j: BackgroundJob) => j.jobId === jobId);

          // Build result object
          const result: BackgroundJobResult = {
            jobId,
            status: payload.status,
            createdCount: payload.created_count,
            updatedCount: payload.updated_count,
            errorCount: payload.error_count,
            payload,
          };

          // Trigger onComplete callback
          if (callbacks?.onComplete) {
            try {
              callbacks.onComplete(result);
            } catch (err) {
              console.error("Error in onComplete callback:", err);
            }
          }

          // Show toast notification
          const isSuccess = payload.status === "completed";
          const isSystemError = payload.status === "error";
          const label = job?.label || payload.label || "Background job";
          const message = isSuccess
            ? `${label} completed`
            : isSystemError
            ? `${label} encountered a system error`
            : `${label} failed`;

          const snackbarKey = enqueueSnackbar(message, {
            variant: isSuccess ? "success" : "error",
            autoHideDuration: 8000,
            // Navigate to jobs page to view results.
            // Phone: the cluster /jobs page isn't tuned for phone and no
            // tuned page surfaces a job-firing entry point on phone, so
            // suppress the CTA. The toast body still surfaces completion.
            action:
              job && !isCompact ? (
                <Button
                  variant="link"
                  size="sm"
                  className="text-inherit"
                  onClick={() => {
                    closeSnackbar(snackbarKey);
                    window.location.href = `/clusters/${job.clusterId}/jobs`;
                  }}
                >
                  View Results
                </Button>
              ) : undefined,
          });
        }
      } catch (err) {
        console.error("Error handling WebSocket message:", err);
      }
    };

    websocket.addEventListener("message", handleMessage);

    return () => {
      websocket.removeEventListener("message", handleMessage);
    };
  }, [websocket, jobs, setJobs, enqueueSnackbar, closeSnackbar, isCompact]);

  // Compute stable key for running clusters (only changes when cluster list changes)
  const runningClustersKey = useMemo(() => {
    const runningJobs = jobs.filter(
      (j: BackgroundJob) => j.status === "pending" || j.status === "running"
    );
    return Array.from(
      new Set(runningJobs.map((j: BackgroundJob) => j.clusterId))
    )
      .sort()
      .join(",");
  }, [jobs]);

  // Poll for heartbeat updates when running jobs exist
  useEffect(() => {
    if (!runningClustersKey) {
      return; // No running jobs, no polling needed
    }

    const clusterIds = runningClustersKey.split(",");

    const pollHeartbeats = async () => {
      try {
        // Poll each cluster in parallel
        const failedClusterIds: string[] = [];
        const results = await Promise.all(
          clusterIds.map(
            async (clusterId) =>
              await backendClient.backgroundJobs
                .listImportJobs(clusterId)
                .catch((err) => {
                  console.warn(
                    `Failed to poll heartbeats for cluster ${clusterId}:`,
                    err
                  );
                  // On 403, mark cluster for job cleanup so we stop polling
                  if (err?.status === 403) {
                    failedClusterIds.push(clusterId);
                  }
                  return []; // Return empty on error, don't break other clusters
                })
          )
        );

        // Remove jobs for clusters the user no longer has access to
        if (failedClusterIds.length > 0) {
          setJobs((prev: BackgroundJob[]) =>
            prev.filter(
              (j: BackgroundJob) => !failedClusterIds.includes(j.clusterId)
            )
          );
        }

        // Flatten results
        const apiJobs = results.flat();

        // Update jobs from API - sync heartbeat, status, and counts
        setJobs((prev: BackgroundJob[]) => {
          let hasChanges = false;
          const updated = prev.map((job: BackgroundJob) => {
            const apiJob = apiJobs.find((aj) => aj.id === job.jobId);
            if (!apiJob) return job;

            // Convert API status (UPPERCASE) to frontend status (lowercase)
            const apiStatus = apiJob.status?.toLowerCase() as JobStatus;
            const heartbeatChanged = apiJob.lastHeartbeat !== job.lastHeartbeat;
            const statusChanged = apiStatus && apiStatus !== job.status;
            // Check if counts need updating (job completed but counts are missing)
            const countsNeedUpdate =
              (apiStatus === "completed" || apiStatus === "failed") &&
              job.createdCount === undefined &&
              job.updatedCount === undefined;

            if (heartbeatChanged || statusChanged || countsNeedUpdate) {
              hasChanges = true;
              return {
                ...job,
                lastHeartbeat: apiJob.lastHeartbeat ?? undefined,
                ...(statusChanged ? { status: apiStatus } : {}),
                // Always sync counts from API for completed/failed jobs
                ...(apiStatus === "completed" || apiStatus === "failed"
                  ? {
                      createdCount: apiJob.createdCount ?? 0,
                      updatedCount: apiJob.updatedCount ?? 0,
                      unchangedCount: apiJob.unchangedCount ?? 0,
                      skippedCount: apiJob.skippedCount ?? 0,
                      errorCount: apiJob.errorCount ?? 0,
                      completedAt: apiJob.completedAt ?? undefined,
                      resultSummary: (apiJob as { resultSummary?: string })
                        .resultSummary,
                    }
                  : {}),
              };
            }
            return job;
          });
          return hasChanges ? updated : prev;
        });
      } catch (err) {
        console.error("Error polling heartbeats:", err);
      }
    };

    // Poll immediately, then every 15 seconds
    pollHeartbeats();
    const intervalId = setInterval(pollHeartbeats, HEARTBEAT_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [runningClustersKey, setJobs]);

  // One-time sync for completed jobs with missing counts (handles page refresh case)
  const completedJobsWithMissingCountsKey = useMemo(() => {
    const jobsToSync = jobs.filter(
      (j: BackgroundJob) =>
        (j.status === "completed" || j.status === "failed") &&
        j.createdCount === undefined &&
        j.unchangedCount === undefined
    );
    return jobsToSync.map((j: BackgroundJob) => j.jobId).join(",");
  }, [jobs]);

  useEffect(() => {
    if (!completedJobsWithMissingCountsKey) {
      return; // No jobs need syncing
    }

    const jobIds = completedJobsWithMissingCountsKey.split(",");

    const syncMissingCounts = async () => {
      try {
        // Fetch each job's full details
        const results = await Promise.all(
          jobIds.map(
            async (jobId: string) =>
              await backendClient.backgroundJobs
                .getImportJob(jobId)
                .catch((err) => {
                  console.warn(`Failed to fetch job ${jobId}:`, err);
                  return null;
                })
          )
        );

        // Update jobs with fetched counts
        setJobs((prev: BackgroundJob[]) => {
          let hasChanges = false;
          const updated = prev.map((job: BackgroundJob) => {
            const apiJob = results.find((r) => r?.id === job.jobId);
            if (!apiJob) return job;

            hasChanges = true;
            return {
              ...job,
              createdCount: apiJob.createdCount ?? 0,
              updatedCount: apiJob.updatedCount ?? 0,
              unchangedCount: apiJob.unchangedCount ?? 0,
              skippedCount: apiJob.skippedCount ?? 0,
              errorCount: apiJob.errorCount ?? 0,
              completedAt: apiJob.completedAt ?? undefined,
              resultSummary: (apiJob as { resultSummary?: string })
                .resultSummary,
            };
          });
          return hasChanges ? updated : prev;
        });
      } catch (err) {
        console.error("Error syncing missing counts:", err);
      }
    };

    syncMissingCounts();
  }, [completedJobsWithMissingCountsKey, setJobs]);

  const value = useMemo(
    () => ({
      jobs,
      registerJob,
      removeJob,
      clearJobs,
      clearCompletedJobs,
      getJobsForCluster,
      getJobsForNamespace,
      getRunningJobsCount,
    }),
    [
      jobs,
      registerJob,
      removeJob,
      clearJobs,
      clearCompletedJobs,
      getJobsForCluster,
      getJobsForNamespace,
      getRunningJobsCount,
    ]
  );

  return (
    <BackgroundJobsContext.Provider value={value}>
      {children}
    </BackgroundJobsContext.Provider>
  );
};
